/**
 * Domain Mapping Form Component
 *
 * Reusable form for creating and editing domain mappings.
 * Validates domain as a proper hostname (no protocol, valid format).
 */

import type { Environment } from "@tatvex/types";
import { Loader2 } from "lucide-react";
import type { ReactElement, FormEvent } from "react";
import { useState, useCallback, useMemo } from "react";

// ============================================================================
// Types
// ============================================================================

/** Domain mapping form values */
export interface DomainMappingFormValues {
  readonly domain: string;
  readonly environment: Environment;
  readonly isPrimary: boolean;
}

/** Props for the DomainMappingForm component */
export interface DomainMappingFormProps {
  /** Initial form values for edit mode */
  readonly initialValues?: Partial<DomainMappingFormValues>;
  /** Submit handler — receives validated form values */
  readonly onSubmit: (values: DomainMappingFormValues) => void;
  /** Cancel handler */
  readonly onCancel: () => void;
  /** Whether the form is currently submitting */
  readonly isSubmitting: boolean;
  /** Error message from the API */
  readonly error?: string;
  /** Whether this is edit mode (vs create mode) */
  readonly isEdit?: boolean;
}

// ============================================================================
// Validation
// ============================================================================

/** Hostname regex — no protocol, no path, no port. Valid domain only. */
const HOSTNAME_REGEX = /^(?!-)[a-zA-Z0-9-]{1,63}(?<!-)(\.[a-zA-Z0-9-]{1,63})*\.[a-zA-Z]{2,}$/;

function validateDomain(domain: string): string | undefined {
  if (!domain.trim()) {
    return "Domain is required";
  }
  if (domain.includes("://")) {
    return "Domain must not include protocol (e.g., use example.com, not https://example.com)";
  }
  if (!HOSTNAME_REGEX.test(domain)) {
    return "Invalid domain format. Must be a valid hostname (e.g., example.com)";
  }
  return undefined;
}

// ============================================================================
// Component
// ============================================================================

export function DomainMappingForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  error,
  isEdit = false,
}: DomainMappingFormProps): ReactElement {
  const [domain, setDomain] = useState(initialValues?.domain ?? "");
  const [environment, setEnvironment] = useState<Environment>(initialValues?.environment ?? "dev");
  const [isPrimary, setIsPrimary] = useState(initialValues?.isPrimary ?? false);
  const [domainError, setDomainError] = useState<string | undefined>(undefined);

  const hasChanges = useMemo(() => {
    if (!isEdit) return true;
    return (
      domain !== (initialValues?.domain ?? "") ||
      environment !== (initialValues?.environment ?? "dev") ||
      isPrimary !== (initialValues?.isPrimary ?? false)
    );
  }, [domain, environment, isPrimary, initialValues, isEdit]);

  const handleSubmit = useCallback(
    (e: FormEvent): void => {
      e.preventDefault();

      const validationError = validateDomain(domain);
      if (validationError) {
        setDomainError(validationError);
        return;
      }

      setDomainError(undefined);
      onSubmit({ domain: domain.toLowerCase().trim(), environment, isPrimary });
    },
    [domain, environment, isPrimary, onSubmit]
  );

  const handleDomainChange = useCallback((value: string): void => {
    setDomain(value);
    setDomainError(undefined);
  }, []);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Domain */}
      <div>
        <label htmlFor="domain" className="block text-sm font-medium mb-1">
          Domain
        </label>
        <input
          id="domain"
          type="text"
          value={domain}
          onChange={(e) => handleDomainChange(e.target.value)}
          placeholder="example.com"
          className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
            domainError
              ? "border-error focus:border-error focus:ring-error"
              : "border-border bg-background focus:border-primary focus:ring-primary"
          }`}
          disabled={isSubmitting}
        />
        {domainError && <p className="mt-1 text-xs text-error">{domainError}</p>}
        <p className="mt-1 text-xs text-foreground-muted">
          Enter the hostname without protocol (e.g., compare.example.com)
        </p>
      </div>

      {/* Environment */}
      <div>
        <label htmlFor="environment" className="block text-sm font-medium mb-1">
          Environment
        </label>
        <select
          id="environment"
          value={environment}
          onChange={(e) => setEnvironment(e.target.value as Environment)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          disabled={isSubmitting}
        >
          <option value="dev">Development</option>
          <option value="staging">Staging</option>
          <option value="prod">Production</option>
        </select>
      </div>

      {/* Primary */}
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
            className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
            disabled={isSubmitting}
          />
          <span className="text-sm font-medium">Primary domain</span>
        </label>
        <p className="mt-1 ml-6 text-xs text-foreground-muted">
          If enabled, this will be the primary domain for this tenant and environment. Only one
          primary domain is allowed per tenant per environment.
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md border border-error bg-error/10 p-3">
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button type="submit" disabled={isSubmitting || !hasChanges} className="btn btn-primary">
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {isEdit ? "Updating..." : "Creating..."}
            </>
          ) : isEdit ? (
            "Update Domain"
          ) : (
            "Add Domain"
          )}
        </button>
      </div>
    </form>
  );
}
